/**
 * Learning Path API
 *
 * Mock API for learning path operations.
 */

import { success, error, withDelay, generateId, timestamp } from './index'
import type { ApiResponse } from '../types'
import type { LearningPath, UserPathProgress } from '../types/learningPath.types'

const mockPaths: LearningPath[] = [
  {
    id: 'path-001',
    title: 'Outdoor Explorer Beginner',
    description: 'Start your outdoor adventure journey with these foundational challenges. Learn to observe nature, navigate trails, and document your discoveries.',
    coverImageUrl: '',
    challengeIds: ['ch-001', 'ch-002', 'ch-003', 'ch-004'],
    checkpoints: [
      { title: 'Nature Observer', description: 'Complete your first two outdoor challenges', afterChallengeIndex: 1, bonusXp: 50 },
      { title: 'Trail Ready', description: 'Complete all four outdoor challenges', afterChallengeIndex: 3, bonusXp: 100 },
    ],
    difficulty: 'beginner',
    estimatedDuration: '2-3 weeks',
    status: 'published',
    category: 'outdoor',
    creatorId: 'system',
    enrollmentCount: 245,
    completionCount: 89,
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
  {
    id: 'path-002',
    title: 'Kitchen Confidence',
    description: 'Build your cooking skills from basics to baking. Each challenge builds on the last, teaching fundamental techniques along the way.',
    coverImageUrl: '',
    challengeIds: ['ch-005', 'ch-006', 'ch-007', 'ch-008', 'ch-009'],
    checkpoints: [
      { title: 'Home Cook', description: 'Complete the first three cooking challenges', afterChallengeIndex: 2, bonusXp: 75 },
      { title: 'Kitchen Master', description: 'Complete all five challenges', afterChallengeIndex: 4, bonusXp: 150 },
    ],
    difficulty: 'intermediate',
    estimatedDuration: '3-4 weeks',
    status: 'published',
    category: 'cooking',
    creatorId: 'system',
    enrollmentCount: 178,
    completionCount: 52,
    createdAt: '2024-02-01T10:00:00Z',
    updatedAt: '2024-02-01T10:00:00Z',
  },
  {
    id: 'path-003',
    title: 'Creative Arts Sampler',
    description: 'Explore different artistic mediums from drawing to pottery to photography. Discover which creative outlets resonate with you.',
    coverImageUrl: '',
    challengeIds: ['ch-010', 'ch-011', 'ch-012'],
    checkpoints: [
      { title: 'Creative Spirit', description: 'Complete all three arts challenges', afterChallengeIndex: 2, bonusXp: 100 },
    ],
    difficulty: 'beginner',
    estimatedDuration: '2 weeks',
    status: 'published',
    category: 'arts',
    creatorId: 'system',
    enrollmentCount: 312,
    completionCount: 145,
    createdAt: '2024-02-15T10:00:00Z',
    updatedAt: '2024-02-15T10:00:00Z',
  },
  {
    id: 'path-004',
    title: 'Science Discovery Trail',
    description: 'Hands-on science experiments and observations. Perfect for curious minds who want to understand how the world works.',
    coverImageUrl: '',
    challengeIds: ['ch-013', 'ch-014', 'ch-015', 'ch-016'],
    checkpoints: [
      { title: 'Junior Scientist', description: 'Complete your first two experiments', afterChallengeIndex: 1, bonusXp: 50 },
      { title: 'Lab Expert', description: 'Complete all science challenges', afterChallengeIndex: 3, bonusXp: 125 },
    ],
    difficulty: 'intermediate',
    estimatedDuration: '3 weeks',
    status: 'published',
    category: 'science',
    creatorId: 'system',
    enrollmentCount: 156,
    completionCount: 38,
    createdAt: '2024-03-01T10:00:00Z',
    updatedAt: '2024-03-01T10:00:00Z',
  },
]

const mockUserProgress: UserPathProgress[] = []

export const learningPathApi = {
  async getPaths(): Promise<ApiResponse<LearningPath[]>> {
    return withDelay(() => {
      const published = mockPaths.filter(p => p.status === 'published')
      return success(published)
    })
  },

  async getPathById(id: string): Promise<ApiResponse<LearningPath>> {
    return withDelay(() => {
      const path = mockPaths.find(p => p.id === id)
      if (!path) return error('Path not found')
      return success(path)
    })
  },

  async getUserPaths(userId: string): Promise<ApiResponse<UserPathProgress[]>> {
    return withDelay(() => {
      const userPaths = mockUserProgress.filter(p => p.userId === userId)
      return success(userPaths)
    })
  },

  async enrollInPath(pathId: string, userId: string): Promise<ApiResponse<UserPathProgress>> {
    return withDelay(() => {
      const existing = mockUserProgress.find(p => p.pathId === pathId && p.userId === userId && p.status === 'active')
      if (existing) return error('Already enrolled in this path')

      const progress: UserPathProgress = {
        id: generateId(),
        pathId,
        userId,
        currentChallengeIndex: 0,
        completedChallengeIds: [],
        startedAt: timestamp(),
        status: 'active',
        createdAt: timestamp(),
        updatedAt: timestamp(),
      }
      mockUserProgress.push(progress)

      const path = mockPaths.find(p => p.id === pathId)
      if (path) path.enrollmentCount++

      return success(progress)
    })
  },

  async abandonPath(pathId: string, userId: string): Promise<ApiResponse<UserPathProgress>> {
    return withDelay(() => {
      const progress = mockUserProgress.find(p => p.pathId === pathId && p.userId === userId && p.status === 'active')
      if (!progress) return error('No active enrollment found')

      progress.status = 'abandoned'
      progress.updatedAt = timestamp()
      return success(progress)
    })
  },
}
